import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import AppCircularProgress from "../components/common/AppCircularProgress";
import StatusChip from "../components/common/StatusChip";
import QuotationDetailsTab from "../components/quotations/QuotationDetailsTab";
import QuotationPdfTab from "../components/quotations/QuotationPdfTab";
import UpdateQuotationModal from "../components/quotations/UpdateQuotationModal";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getQuotationById,
  convertToInvoice,
  resetQuoteServerStatus,
} from "../redux/actions/quotationActions";
import {
  checkCashierHasOpenShift,
  getCurrentOrRecentShift,
} from "../redux/actions/shiftActions";

const QuotationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { navigateToTab, closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    item: currentQuotation,
    loadingFetchOne: loadingQuotation,
    errorFetchOne: errorLoadingQuotation,
    serverStatus,
    loadingConvert,
  } = useAppSelector((state) => state.quotation);

  const { hasOpenShift, currentShift } = useAppSelector((state) => state.shift);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [isConvertConfirmOpen, setIsConvertConfirmOpen] =
    useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const match = location.pathname.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
    );
    if (match && match[0]) setQuoteId(match[0]);
  }, [location.pathname]);

  useEffect(() => {
    if (quoteId) dispatch(getQuotationById(quoteId));
  }, [dispatch, quoteId]);

  useEffect(() => {
    dispatch(checkCashierHasOpenShift());
    dispatch(getCurrentOrRecentShift());
  }, [dispatch]);

  useEffect(() => {
    if (serverStatus?.operation === "convert" && serverStatus?.status === 201) {
      dispatch(resetQuoteServerStatus());
      if (quoteId) dispatch(getQuotationById(quoteId));
      if (serverStatus.metadata?.invoiceId) {
        navigateToTab(`/invoices/${serverStatus.metadata.invoiceId}`);
      }
    }
  }, [serverStatus, dispatch, navigateToTab, quoteId]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void =>
    setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = (): void => setMenuAnchorEl(null);

  const handleConvertClick = (): void => {
    handleMenuClose();
    if (!hasOpenShift || !currentShift?.id) {
      alert("You must have an active shift to generate an invoice.");
      return;
    }
    if (currentQuotation) {
      setIsConvertConfirmOpen(true);
    }
  };

  const handleConvertConfirm = (): void => {
    if (currentQuotation && currentShift?.id) {
      dispatch(convertToInvoice(currentQuotation.id, currentShift.id));
    }
  };

  if (loadingQuotation) return <AppCircularProgress />;
  if (errorLoadingQuotation)
    return <Alert severity="error">{errorLoadingQuotation}</Alert>;
  if (!currentQuotation) return null;

  const isConverted = currentQuotation.status === "CONVERTED";
  const isAccepted = currentQuotation.status === "ACCEPTED";

  // --- RENDER FUNCTIONS FOR ACTIONS ---
  const renderDesktopActions = (): React.JSX.Element => (
    <Stack direction="row" spacing={2}>
      {!isConverted && (
        <AppButton
          pill
          variant="outlined"
          color="secondary"
          startIcon={<EditIcon />}
          onClick={() => setIsUpdateOpen(true)}
        >
          Edit Quote
        </AppButton>
      )}
      {isAccepted && (
        <AppButton
          pill
          variant="contained"
          color="warning"
          startIcon={<FileCopyIcon />}
          onClick={handleConvertClick}
          disabled={loadingConvert}
        >
          {loadingConvert ? "Converting..." : "Convert to Invoice"}
        </AppButton>
      )}
      {isConverted && currentQuotation.convertedToInvoiceId && (
        <AppButton
          pill
          variant="outlined"
          color="primary"
          onClick={() =>
            navigateToTab(`/invoices/${currentQuotation.convertedToInvoiceId}`)
          }
        >
          View Generated Invoice
        </AppButton>
      )}
    </Stack>
  );

  const renderMobileActions = (): React.JSX.Element => (
    <Box>
      <IconButton onClick={handleMenuOpen} color="inherit">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {!isConverted && (
          <MenuItem
            onClick={() => {
              setIsUpdateOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Edit Quote" />
          </MenuItem>
        )}
        {isAccepted && (
          <MenuItem onClick={handleConvertClick} disabled={loadingConvert}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Convert to Invoice" />
          </MenuItem>
        )}
        {isConverted && currentQuotation.convertedToInvoiceId && (
          <MenuItem
            onClick={() => {
              navigateToTab(
                `/invoices/${currentQuotation.convertedToInvoiceId}`,
              );
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="View Invoice" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Stack direction="row" sx={{ alignItems: "center" }} spacing={2}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/quotations")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{ fontWeight: "bold" }}
            >
              Quotation #{currentQuotation.quoteNumber}
            </Typography>
            {!isMobile && <StatusChip status={currentQuotation.status} />}
          </Stack>

          {isMobile ? renderMobileActions() : renderDesktopActions()}
        </Stack>

        {isMobile && (
          <Box sx={{ mb: 2, pl: 5 }}>
            <StatusChip status={currentQuotation.status} />
          </Box>
        )}

        <Typography
          variant="body2"
          sx={{ color: "text.secondary", ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Issued: {new Date(currentQuotation.issueDate).toLocaleString()} •{" "}
          {currentQuotation.branchName || "Main Branch"}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Quote Details" />
          <Tab label="PDF Document" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <QuotationDetailsTab quote={currentQuotation} />}
        {activeTab === 1 && <QuotationPdfTab quote={currentQuotation} />}
      </Box>

      {isUpdateOpen && (
        <UpdateQuotationModal
          open={isUpdateOpen}
          onClose={() => setIsUpdateOpen(false)}
          quote={currentQuotation}
        />
      )}
      {isConvertConfirmOpen && (
        <ConfirmDialog
          open={isConvertConfirmOpen}
          onClose={() => setIsConvertConfirmOpen(false)}
          onConfirm={handleConvertConfirm}
          title="Convert to Invoice"
          description={`Are you sure you want to convert Quotation #${currentQuotation.quoteNumber} into a live invoice? This will register an active sale against the selected customer.`}
          confirmText="Convert"
          loading={loadingConvert}
        />
      )}
    </Box>
  );
};
export default QuotationPage;
